// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaPayment,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  regattaPaymentStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegattaPaymentFields from '../../fields/RegattaPaymentFields';
import ThQty from '../../common/ThQty';
import ThSort from '../../common/ThSort';

// Services
import Services from '../../../services';

// Types
type $Props = $Translate;

type $State = {
  data: Array<$FullRegattaPayment>;
  dataSortConfig: null | string;
  loading: boolean;
};

const pageId = adminPages.adminRegattaPaymentsPage;

class AdminRegattaPaymentsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataSortConfig: null,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'RegattaPayment') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminRegattaPayment();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async sortData(newDataSortConfig: string): Promise<void> {
    const {
      data,
    } = this.state;

    const [key, direction] = newDataSortConfig.split(':');

    await this.setStateAsync({
      loading: true,
    });

    const sortedData = _.orderBy(
      data,
      [key],
      [direction === 'desc' ? 'desc' : 'asc'],
    );

    await this.setStateAsync({
      data: sortedData,
      dataSortConfig: newDataSortConfig,
      loading: false,
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader title={t('Payments')} />
      </Card>
    );
  }

  renderButtons(dataItem: $FullRegattaPayment) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaPaymentFields item={dataItem} />
          )}
          id={`update-regatta-payment-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$FullRegattaPayment>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminRegattaPayment(params);
            },
          })}
          subTitle={''}
          title={t('Regatta payment')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-payment-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-payment-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminRegattaPayment(dataItem),
          })}
          subTitle={dataItem.id}
          title={t('Regatta payment')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-payment-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
      dataSortConfig,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Regatta')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Regatta.Name'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Status')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Status'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Paid')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'PaymentIntent'}
            />
            <th>{t('Discount')}</th>
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Payment ID')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'PaymentId'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Date created')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'createdAt'}
            />
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $FullRegattaPayment) => {
            const paymentIntent = JSON.parse(dataItem.PaymentIntent);

            return (
              <tr key={dataItem.id}>
                <td>{dataItem.Regatta && dataItem.Regatta.Name}</td>
                <td>{t(regattaPaymentStatusLabel[dataItem.Status])}</td>
                <td>{`${paymentIntent.amount / 100} ${paymentIntent.currency}`}</td>
                <td>
                  {dataItem.RegattaPaymentDiscount && `${dataItem.RegattaPaymentDiscount.Name} ${dataItem.RegattaPaymentDiscount.Value / 100} ${paymentIntent.currency}`}
                </td>
                <td>{dataItem.PaymentId}</td>
                <TdDate
                  duration
                  multiple
                  value={dataItem.createdAt}
                />
                <td>
                  {this.renderButtons(dataItem)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminRegattaPaymentsPageView;
